<script setup>
import MarketSnapshot from 'components/market/MarketSnapshot.vue';
import MarketSpecialisationTable from 'components/market/MarketSpecialisationTable.vue';
import { usePageMeta } from 'composables/page-meta';
import marketConfig, { MARKET_LOCATIONS, TECH_MARKET_CLASSIFICATION_SLUG } from 'src/configs/market';
import { createPageNavigation } from 'src/utils/error-page';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { trackProductEvent } from 'src/utils/umami-util';
import { buildXprtJobsCategoryUrl } from 'src/utils/xprt-jobs-route-util';
import { useMarketStore } from 'stores/market-store';
import { computed } from 'vue';

defineOptions({
    async preFetch({ currentRoute, ssrContext, store }) {
        const navigation = createPageNavigation({ currentRoute, ssrContext, store });
        const marketStore = useMarketStore();
        try {
            await marketStore.loadOverview();
        } catch {
            // The page keeps its scope and methodology while the statistics are out of reach, and answers 503 so a
            // crawler comes back rather than indexing the unavailable state.
            navigation.raiseError(503);
        }
    }
});

const marketStore = useMarketStore();

const isMarketAvailable = computed(() => marketStore.overview !== null && marketStore.overviewError === null);

function trackJobsHandoff() {
    trackProductEvent('xprt_jobs_handoff', {
        component: 'market_overview',
        classificationSlug: TECH_MARKET_CLASSIFICATION_SLUG,
        locationSlug: marketConfig.countrySlug
    });
}

usePageMeta(() => buildPageMeta({
    title: 'Australian ICT Job Market & Salary Insights',
    description: 'Compare Australian ICT job demand, advertised salary ranges, hiring employers and work arrangements '
        + 'across specialisations in Xprt Jobs listings.',
    canonicalUrl: 'https://xprt.dev/market'
}));
</script>

<template>
    <main class="market-overview-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <div class="hero-copy q-py-lg">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                        Australia · Information &amp; communication technology · Active listings
                    </p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">The ICT market, measured from live roles.</h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-mt-none q-mb-lg">
                        Advertised pay, demand and hiring activity across every Australian technology specialisation,
                        with a note beside every figure explaining how it is counted.
                    </p>
                    <div class="row q-gutter-sm">
                        <q-btn
                            :href="buildXprtJobsCategoryUrl(TECH_MARKET_CLASSIFICATION_SLUG)"
                            color="primary"
                            data-test="overview-jobs-link"
                            icon-right="sym_r_north_east"
                            label="View jobs on Xprt Jobs"
                            no-caps
                            rel="noopener noreferrer"
                            target="_blank"
                            unelevated
                            @click="trackJobsHandoff"
                        />
                        <q-btn color="primary" href="/methodology" label="Read the notes" no-caps outline/>
                    </div>
                </div>
            </div>
        </header>
        <div class="container-md q-px-md q-py-xl">
            <q-banner
                v-if="marketStore.overviewError && !marketStore.overview"
                class="unavailable-banner q-mb-xl"
                role="status"
            >
                <template #avatar>
                    <q-icon color="primary" name="sym_r_cloud_off"/>
                </template>
                <strong>The current market snapshot is unavailable.</strong>
                <div class="text-body2 q-mt-xs">
                    The notes remain available while the next data refresh completes.
                </div>
            </q-banner>
            <MarketSnapshot :is-available="isMarketAvailable" :show-scope="false" :snapshot="marketStore.overview" show-trend/>
            <q-separator class="q-my-xl"/>
            <MarketSpecialisationTable
                :is-available="isMarketAvailable"
                :rows="marketStore.overview?.subclassifications || []"
            />
            <q-separator class="q-my-xl"/>
            <section aria-labelledby="market-states-heading">
                <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">By state and territory</p>
                <h2 id="market-states-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">
                    Where in Australia the roles are.
                </h2>
                <p class="text-body1 text-hint q-mt-none q-mb-lg">
                    Each page reads the same evidence for one state or territory. Listings resolve to the state they
                    sit in, so these describe a whole state rather than any one city within it.
                </p>
                <ul class="state-list q-pl-none q-my-none">
                    <li v-for="location of MARKET_LOCATIONS" :key="location.slug" class="q-py-sm">
                        <a :href="`/market/in/${location.slug}`" class="text-weight-medium">{{ location.name }}</a>
                    </li>
                </ul>
            </section>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.market-overview-page {
    .hero-copy {
        max-width: 760px;
    }

    .hero-deck {
        max-width: 640px;
    }

    .state-list {
        columns: 2;
        list-style: none;

        @media (max-width: $breakpoint-xs-max) {
            columns: 1;
        }
    }

    .unavailable-banner {
        background: rgba($primary, 0.06);
        border: 1px solid rgba($primary, 0.16);
    }
}
</style>
